import config from 'app/config';
import template from './opform.tpl.html';
import 'app/common/styles/_opform.scss';

// Caches declared outside the class, accessible to all instances.
const formFetchCache = new Map();
const scriptLoadCache = new Map();

class OntraportFormController {

  static $inject = [
    "$window", "$ocLazyLoad", "$sce", "$timeout", "userService", "PageService", "$element", "$q"
  ];

  /** @ngInject */
  constructor($window, $ocLazyLoad, $sce, $timeout, userService, PageService, $element, $q) {
    Object.assign(this, {
      $window, $ocLazyLoad, $sce, $timeout, userService, PageService, $element, $q
    });
    this.config = config;

    this.page = null;
    this.opformConfig = {};
    this.trustedHtmlForm = '';
    this.isLoading = false;
    this.hasError = false;
  }

  $onInit() {
    this.initializeForm();
  }

  async initializeForm() {
    if (this.isLoading || this.trustedHtmlForm) return;

    let formId = null;
    let ontraportScriptPath = null;

    try {
      this._resetFormState();

      // **Step 1+2: Fetch page & form ID ASAP, but not strictly await (since this.PageService.getGlobalPage might be sync)**
      await this._getAndSetPage();
      formId = this._getFormId();

      if (!formId) throw new Error('Ontraport form ID is not defined in opformConfig.');

      // **Step 3+4: Kick off data fetch and script load IN PARALLEL**
      const fetchFormPromise = this._fetchFormData(formId);

      ontraportScriptPath = `https://forms.ontraport.com/v2.4/include/formEditor/genjs-v3.php?html=false&uid=${formId}`;
      const loadScriptPromise = this._loadOntraportScript(ontraportScriptPath);

      // **Only need form HTML to render, but load both in parallel (since script is usually cached, doesn't block DOM render)**
      const formDataResponse = await fetchFormPromise;
      // Start sanitizing HTML ASAP (non-blocking)
      const rawHtmlString = formDataResponse.data;
      this.trustedHtmlForm = this._sanitizeAndTrustHtml(rawHtmlString);

      // Now make sure the script load completed (in case it was still running)
      await loadScriptPromise;

      // **Start prefill ASAP, do not block on render**
      const queryParams = this.userService.getQueryParamObject();
      this._prefillFormWhenReady(queryParams);

    } catch (error) {
      this._handleInitializationError(error, formId, ontraportScriptPath);
    } finally {
      this.isLoading = false;
    }
  }

  _resetFormState() {
    this.isLoading = true;
    this.hasError = false;
  }

  async _getAndSetPage() {
    // Micro-optimization: Avoid an 'await' if not needed
    let currentPage = this.PageService.getGlobalPage();
    if (currentPage && typeof currentPage.then === 'function') {
      currentPage = await currentPage;
    }
    this.page = currentPage;
  }

  _getFormId() {
    const pageFormConfig = this.page?.options?.hsform || {};
    this.opformConfig = {
      formid: this.formid || pageFormConfig.id,
      btntext: this.btntext || this.page?.options?.button?.text,
      properties: this.properties || pageFormConfig.properties,
      btnclass: this.btnclass || 'btn btn-lg btn-warning mt-0',
      email: this.userService.getQueryParam('email', '') || this.userService.getQueryParam('Email', '')
    };
    return this.opformConfig.formid;
  }

  async _fetchFormData(formId) {
    // Unchanged (already optimal)
    let apiResponse;
    if (formFetchCache.has(formId)) {
      apiResponse = await formFetchCache.get(formId);
    } else {
      const fetchDataPromise = this.$q.resolve().then(async () => {
        const basepath = config.appName === 'mrs' ? 'page' : config.appName;
        const apiUrl = `${basepath}/op/${config.appName}/form/${formId}`;
        const rawApiResult = await this.PageService.getRowV3(apiUrl);
        const { status, data: htmlContent } = rawApiResult;
        if (status !== 'ok' || !htmlContent) {
          throw new Error(`Failed to load form data for FormId ${formId}. Server response: ` + JSON.stringify(rawApiResult));
        }
        if (typeof htmlContent !== 'string') {
          rawApiResult.data = String(htmlContent || '');
        }
        return rawApiResult;
      });
      formFetchCache.set(formId, fetchDataPromise);
      apiResponse = await fetchDataPromise;
    }
    return apiResponse;
  }

  async _loadOntraportScript(scriptPath) {
    if (scriptLoadCache.has(scriptPath)) {
      await scriptLoadCache.get(scriptPath);
    } else {
      // Note: ocLazyLoad returns a promise already
      const loadScriptPromise = this.$ocLazyLoad.load({ type: 'js', path: scriptPath });
      scriptLoadCache.set(scriptPath, loadScriptPromise);
      await loadScriptPromise;
    }
  }

  _handleInitializationError(error, formId, ontraportScriptPath) {
    console.error("[OntraportFormController] Form initialization error:", error);
    this.hasError = true;
    this.isLoading = false;
    if (formId && formFetchCache.has(formId)) formFetchCache.delete(formId);
    if (ontraportScriptPath && scriptLoadCache.has(ontraportScriptPath)) scriptLoadCache.delete(ontraportScriptPath);
  }

  _sanitizeAndTrustHtml(htmlString) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    tempDiv.querySelectorAll('script, link').forEach(tag => tag.remove());
    return this.$sce.trustAsHtml(tempDiv.innerHTML);
  }

  // *** FASTER PREFILL ***
  // (Tries more frequently, shorter total time, avoids missing form if it renders after script is loaded)
  _prefillFormWhenReady(queryParams, retries = 15, delay = 100) {
    if (retries <= 0) return;
    this.$timeout(() => {
      const formElement = this.$element[0].querySelector(
        `form input[type="hidden"][name="uid"][value="${this.opformConfig.formid}"]`
      )?.closest('form');

      if (formElement) {
        const fieldsToFill = [
          'firstname', 'fname', 'lastname', 'lname', 'email', 'contact_id', 'sms_number',
          'utm_content', 'utm_source', 'utm_medium', 'utm_term', 'utm_campaign',
          'referral_page', '_op_gclid', '_op_gcid', '_fbc', '_fbp', '_op_li_fat_id'
        ];
        fieldsToFill.forEach(prop => {
          const value = queryParams[prop] || queryParams[prop.charAt(0).toUpperCase() + prop.slice(1)] || '';
          if (value) {
            const nodes = formElement.querySelectorAll(`[name="${prop}"], [name="primary_contact//${prop}"]`);
            nodes.forEach(node => {
              if (node.value === '' || node.value === node.placeholder) {
                node.value = value;
                node.dispatchEvent(new Event('input', { bubbles: true }));
                node.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          }
        });
      } else {
        // Retry with reduced delay for more rapid prefill after render
        this._prefillFormWhenReady(queryParams, retries - 1, delay);
      }
    }, delay);
  }
}


// --- Component Definition ---
class OntraportFormComponent {
  constructor() {
    this.bindings = {
      formid: '@?',
      btntext: '@?',
      formtarget: '@?',
      btnclass: '@?',
      properties: '=?',
    };
    this.template = template;
    this.controller = OntraportFormController;
  }
}

export default angular
  .module('opform.component', [])
  .component('opform', new OntraportFormComponent())
  .name;